import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Bookmark, WifiOff } from "lucide-react";
import { isSaved, toggleSave, isOfflineAvailable, useLibraryVersion } from "@/lib/library";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["ALL", "VLMAG", "VLMAG MUSIC", "VLMAG STYLE"];
const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "shared", label: "Most Shared" },
];

function sortStories(list, sort) {
  const arr = [...list];
  if (sort === "oldest") arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  else if (sort === "shared") arr.sort((a, b) => (b.share_clicks || 0) - (a.share_clicks || 0) || (b.created_at || "").localeCompare(a.created_at || ""));
  else arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  return arr;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function mediaUrl(src) {
  if (!src) return "";
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
}

export const Stories = () => {
  const [stories, setStories] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [hasSeries, setHasSeries] = useState(false);
  useLibraryVersion();

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/series`)
      .then((r) => active && setHasSeries((r.data.series || []).length > 0))
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/stories`, { params: filter === "ALL" ? {} : { network: filter } })
      .then((r) => active && setStories(r.data))
      .catch(() => active && setStories([]))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [filter]);

  return (
    <section id="stories" data-testid="stories" className="px-5 md:px-10 py-20 md:py-32 border-t border-white/10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-4">Original Features</p>
          <h2 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl md:text-7xl">
            Stories From<br />the Culture
          </h2>
          {hasSeries && (
            <Link
              to="/series"
              data-testid="stories-series-link"
              className="group inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-[0.2em] font-bold text-white hover:text-[#E30613] transition-colors"
            >
              Browse The Series
              <ArrowUpRight size={16} className="text-[#E30613] group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-5">
          <div className="flex flex-wrap gap-2" data-testid="stories-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors duration-300 ${
                  filter === f ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/20 text-neutral-400 hover:border-white/60 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 sm:border-l sm:border-white/10 sm:pl-5" data-testid="stories-sort">
            {SORTS.map((s) => (
              <button
                key={s.key}
                data-testid={`sort-${s.key}`}
                onClick={() => setSort(s.key)}
                className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors duration-300 ${
                  sort === s.key ? "text-[#E30613]" : "text-neutral-500 hover:text-white"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
          {[0, 1, 2].map((i) => (
            <div key={i} className="aspect-[4/5] bg-neutral-900 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="stories-grid">
          {sortStories(stories, sort).map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={`story-${s.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
              className="group relative border border-white/10 hover:border-[#E30613] transition-colors duration-500"
            >
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSave(s); }}
                data-testid={`story-save-${s.id}`}
                aria-label={isSaved(s.id) ? "Remove from saved" : "Save for later"}
                className={`absolute top-0 right-0 z-10 p-2.5 border-l border-b border-white/10 bg-black/85 backdrop-blur transition-colors ${isSaved(s.id) ? "text-[#E30613]" : "text-neutral-400 hover:text-white"}`}
              >
                <Bookmark size={16} fill={isSaved(s.id) ? "#E30613" : "none"} />
              </button>
              <Link to={`/story/${s.id}`} data-testid={`story-link-${s.id}`} className="block cursor-pointer">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={mediaUrl(s.image)} alt={s.title} className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-0 left-0 bg-black px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#E30613] border-r border-b border-white/10">
                    {s.network}
                  </span>
                  {isOfflineAvailable(s.id) && (
                    <span className="absolute bottom-0 left-0 inline-flex items-center gap-1.5 bg-black/85 backdrop-blur px-2.5 py-1.5 text-[9px] uppercase tracking-[0.15em] font-bold text-emerald-400 border-r border-t border-white/10" data-testid={`offline-badge-${s.id}`}>
                      <WifiOff size={11} /> Offline Ready
                    </span>
                  )}
                  {s.series && (
                    <span className="absolute bottom-0 right-0 bg-[#E30613] px-3 py-1.5 text-[9px] uppercase tracking-[0.18em] font-bold text-white" data-testid={`story-series-${s.id}`}>
                      {s.series}{s.part ? ` · Pt ${s.part}` : ""}
                    </span>
                  )}
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{s.category}</span>
                    <ArrowUpRight size={16} className="text-neutral-600 group-hover:text-[#E30613] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display uppercase text-xl md:text-2xl leading-[0.95] tracking-tight text-white group-hover:text-[#E30613] transition-colors duration-300">
                    {s.title}
                  </h3>
                  <p className="text-sm text-neutral-400 mt-3 leading-relaxed">{s.excerpt}</p>
                  <div className="flex items-center justify-between mt-5">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-600">{s.author}</p>
                    {fmtDate(s.created_at) && (
                      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-600" data-testid={`story-date-${s.id}`}>{fmtDate(s.created_at)}</p>
                    )}
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      )}
    </section>
  );
};
